import React, { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SegmentedControl, Table, Text } from '@mantine/core';
import { Activity, FileText, List, Settings, UtensilsCrossed } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pipe } from 'remeda';
import { HeartRateCharts, HeartRateSkeleton } from '../index';
import StateChangesPanel from '../../../../components/StateChangesPanel';
import { useHeartRateStore } from '@store/useHeartRateStore';
import { getConfigurationTemplateSafe } from '@services/configurationTemplateManager';
import { SessionConfigurationView } from '../SessionConfigurationView/SessionConfigurationView';
import { ExposureSessionPanel } from '../ExposureSessionPanel/ExposureSessionPanel';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { getExposureConfigFromSession } from '../../../../utils/extractSessionObject';
import {
  getPortateFromExposureConfig,
  type ExposureFoodDisplayContext,
} from '../../../../utils/exposureMultiItem';

interface SessionChartsProps {
  session: DeviceConfigurationApi;
}

type TabValue = 'heartRate' | 'hrv' | 'exposure' | 'configuration' | 'answers';

export const SessionCharts = React.memo<SessionChartsProps>(({ session }) => {
  const { t } = useTranslation();
  const events = useHeartRateStore(s => s.events);
  const hasEvents = useDeferredValue(events.length > 0);
  const status = session?.status;
  const [activeTab, setActiveTab] = useState<TabValue>('heartRate');

  // Query per verificare se la configurazione è disponibile
  const { data: configurationTemplate } = useQuery({
    queryKey: ['session-configuration', session?.id],
    queryFn: async () => {
      if (!session?.id) return null;
      return await getConfigurationTemplateSafe(session.id);
    },
    enabled: Boolean(session?.id),
  });

  // Mostra il tab configurazione solo se la chiamata è andata a buon fine (200)
  const showConfigurationTab = Boolean(configurationTemplate);

  const exposureFoodDisplay = useMemo((): ExposureFoodDisplayContext => {
    if (session?.type !== 'exposure') {
      return { isMultiItem: false, portate: [] };
    }
    const config = getExposureConfigFromSession(session);
    return {
      isMultiItem: Boolean(config.isMultiItem),
      portate: getPortateFromExposureConfig(config),
    };
  }, [session]);

  const hasQuestions = useMemo(() => {
    if (!session?.config) return false;

    const extractSteps = (config: unknown): unknown[] => {
      if (Array.isArray(config)) return config;

      if (typeof config === 'object' && config !== null) {
        const objectConfig = config as Record<string, unknown>;

        if (Array.isArray(objectConfig.steps)) {
          return objectConfig.steps;
        }

        const numericValues = Object.entries(objectConfig)
          .filter(([key]) => /^\d+$/.test(key))
          .map(([, value]) => value);

        return numericValues;
      }

      if (typeof config === 'string') {
        try {
          const parsed = JSON.parse(config);
          return extractSteps(parsed);
        } catch {
          return [];
        }
      }

      return [];
    };

    const isQuestionStep = (step: unknown): boolean => {
      const stepObj = step as { type?: string; question?: unknown };
      const hasType =
        stepObj?.type === 'QUESTION' || stepObj?.type === 'question';
      const hasQuestion = stepObj?.question !== undefined;
      return hasType || hasQuestion;
    };

    return pipe(session.config, extractSteps, steps =>
      steps.some(isQuestionStep)
    );
  }, [session?.config]);

  const isExposure = session?.type === 'exposure';

  const tabData = [
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Activity size={16} />
          <span>{t('measurement_chart_title', 'Grafico Misurazioni')}</span>
        </div>
      ),
      value: 'heartRate' as TabValue,
    },
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <List size={16} />
          <span>{t('state_changes_title', 'Elenco Stati')}</span>
        </div>
      ),
      value: 'hrv' as TabValue,
    },
  ];

  if (isExposure) {
    tabData.splice(1, 0, {
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <UtensilsCrossed size={16} />
          <span>{t('exposure_items_tab', 'Elementi')}</span>
        </div>
      ),
      value: 'exposure' as TabValue,
    });
  }

  if (hasQuestions) {
    tabData.push({
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FileText size={16} />
          <span>Elenco risposte</span>
        </div>
      ),
      value: 'answers' as TabValue,
    });
  }

  // Aggiungi il tab configurazione solo se disponibile
  if (showConfigurationTab) {
    tabData.push({
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Settings size={16} />
          <span>Configurazione</span>
        </div>
      ),
      value: 'configuration' as TabValue,
    });
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <SegmentedControl
        value={activeTab}
        onChange={value => setActiveTab(value as TabValue)}
        data={tabData}
        style={{ flexShrink: 0, marginBottom: '1rem' }}
      />

      <Card w={'100%'} h={'100%'} p='0' style={{ flex: 1, minHeight: 0 }}>
        {activeTab === 'heartRate' && (
          <div
            style={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {status !== 'completed' && !hasEvents ? (
              <HeartRateSkeleton />
            ) : (
              <HeartRateCharts title={t('chart', 'Grafici misurazioni')} />
            )}
          </div>
        )}

        {activeTab === 'hrv' && (
          <div
            style={{
              paddingTop: '1rem',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <StateChangesPanel
              sessionType={session?.type}
              exposureFoodDisplay={exposureFoodDisplay}
            />
          </div>
        )}

        {activeTab === 'exposure' && isExposure && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <ExposureSessionPanel session={session} />
          </div>
        )}

        {activeTab === 'answers' && hasQuestions && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              padding: '1rem',
            }}
          >
            <AnswersListPanel session={session} />
          </div>
        )}

        {activeTab === 'configuration' && showConfigurationTab && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <SessionConfigurationView sessionId={session.id} />
          </div>
        )}
      </Card>
    </div>
  );
});

SessionCharts.displayName = 'SessionCharts';

interface AnswersListPanelProps {
  session: DeviceConfigurationApi;
}

const AnswersListPanel: React.FC<AnswersListPanelProps> = ({ session }) => {
  const sessionAnswers = useMemo(() => {
    if (!session?.answers) return [];
    try {
      const parsed = JSON.parse(session.answers);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing session answers:', error);
      return [];
    }
  }, [session?.answers]);

  const formatTimestamp = (timestamp: number | string) => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Card withBorder shadow='sm' radius='sm'>
      <Text size='sm' fw={500} mb='md'>
        Risposte ({sessionAnswers.length})
      </Text>

      {sessionAnswers.length === 0 ? (
        <Text size='xs' c='dimmed'>
          Nessuna risposta disponibile
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Orario</Table.Th>
              <Table.Th>Domanda</Table.Th>
              <Table.Th>Risposta</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sessionAnswers.map((answer: any, index: number) => (
              <Table.Tr key={`answer-${answer.stepId ?? index}-${index}`}>
                <Table.Td>
                  <Text size='xs'>{formatTimestamp(answer.timestamp)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size='xs'>{answer.question || '--'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size='xs'>{answer.answer || '--'}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
};
